import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Toaster } from '@/components/ui/sonner';
import { SelectedDatasetsPanel } from '@/components/SelectedDatasetsPanel';
import { TutorialOverlay } from '@/components/TutorialOverlay';
import { WelcomeScreen } from '@/components/WelcomeScreen';
import { KeyboardShortcutsHelp } from '@/components/KeyboardShortcutsHelp';
import { AppHeader } from '@/components/AppHeader';
import { AppMainContent } from '@/components/AppMainContent';

import { mockAPISources } from '@/lib/mockData';
import { DatabaseIcon } from '@phosphor-icons/react';
import { useAppLogic } from './hooks/useAppLogic';

function App() {
  const {
    selectedDatasets,
    setSelectedDatasets,
    activeTab,
    setActiveTab,
    selectedSource,
    setSelectedSource,
    previewDataset,
    setPreviewDataset,
    qualityReports,
    setQualityReports,
    correlations,
    filteredSources,
    setFilteredSources,
    appliedTemplate,
    activeTutorial,
    showKeyboardHelp,
    setShowKeyboardHelp,
    selectedPanelExpanded,
    setSelectedPanelExpanded,
    allDatasets,
    totalDatasetCount,
    hasVisualizations,
    hasExports,
    isLoading,
    tutorialsWithCallbacks,
    handleSourceSelect,
    handleDatasetSelect,
    handleAnalyzeCorrelations,
    handleGlobalSearch,
    handleSuggestionClick,
    handleRemoveDataset,
    handleDatasetPreview,
    handleFilterChange,
    handleTemplateSelect,
    handleClearTemplate,
    handleTutorialComplete,
    handleTutorialStart,
    handleBulkCorrelationAnalysis,
    handleDataExport,
  } = useAppLogic();

  return (
    <div className='min-h-screen bg-background overflow-x-hidden max-w-full'>
      {/* Header */}
      <AppHeader
        mockAPISources={mockAPISources}
        allDatasets={allDatasets}
        selectedDatasets={selectedDatasets}
        activeTab={activeTab}
        qualityReports={qualityReports}
        correlations={correlations}
        appliedTemplate={appliedTemplate}
        tutorialsWithCallbacks={tutorialsWithCallbacks}
        onGlobalSearch={handleGlobalSearch}
        onTabChange={setActiveTab}
        onShowKeyboardHelp={() => setShowKeyboardHelp(true)}
        onTutorialStart={handleTutorialStart}
        onAnalyzeCorrelations={handleAnalyzeCorrelations}
      />

      {/* Selected Datasets Section */}
      {selectedDatasets.length > 0 && (
        <SelectedDatasetsPanel
          datasets={selectedDatasets}
          onRemoveDataset={handleRemoveDataset}
          onClearAll={() => setSelectedDatasets([])}
          onPreviewDataset={handleDatasetPreview}
          isExpanded={selectedPanelExpanded}
          onToggleExpand={() =>
            setSelectedPanelExpanded(!selectedPanelExpanded)
          }
          qualityReports={qualityReports}
          onQualityReportsUpdate={setQualityReports}
          onExport={handleDataExport}
          onCorrelationAnalysis={handleBulkCorrelationAnalysis}
        />
      )}

      {/* Main Content */}
      <AppMainContent
        activeTab={activeTab}
        selectedDatasets={selectedDatasets}
        qualityReports={qualityReports}
        correlations={correlations}
        appliedTemplate={appliedTemplate}
        selectedSource={selectedSource}
        hasVisualizations={hasVisualizations}
        hasExports={hasExports}
        onTabChange={setActiveTab}
        onSuggestionClick={handleSuggestionClick}
        onShowKeyboardHelp={() => setShowKeyboardHelp(true)}
        filteredSources={filteredSources}
        allDatasets={allDatasets}
        totalDatasetCount={totalDatasetCount}
        isLoading={isLoading}
        handleTemplateSelect={handleTemplateSelect}
        handleDatasetSelect={handleDatasetSelect}
        handleDatasetPreview={handleDatasetPreview}
        handleSourceSelect={handleSourceSelect}
        handleFilterChange={handleFilterChange}
        handleAnalyzeCorrelations={handleAnalyzeCorrelations}
        handleClearTemplate={handleClearTemplate}
        handleDataExport={handleDataExport}
        setSelectedDatasets={setSelectedDatasets}
        setFilteredSources={setFilteredSources}
        setSelectedSource={setSelectedSource}
      />

      {/* Dataset Preview Dialog */}
      <Dialog
        open={!!previewDataset}
        onOpenChange={() => setPreviewDataset(null)}
      >
        <DialogContent className='max-w-4xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <DatabaseIcon className='w-5 h-5' />
              {previewDataset?.name}
            </DialogTitle>
            <DialogDescription>{previewDataset?.description}</DialogDescription>
          </DialogHeader>

          {previewDataset && (
            <div className='space-y-4'>
              <div>
                <h4 className='font-semibold mb-2'>Schema</h4>
                <div className='overflow-x-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Field</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Nullable</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewDataset.schema.map(field => (
                        <TableRow key={field.name}>
                          <TableCell className='font-mono text-xs min-w-0 break-all'>
                            {field.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant='outline' className='text-xs'>
                              {field.type}
                            </Badge>
                          </TableCell>
                          <TableCell className='text-xs'>
                            {field.nullable ? 'Yes' : 'No'}
                          </TableCell>
                          <TableCell className='text-xs text-muted-foreground max-w-48 truncate'>
                            {field.description || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div>
                <h4 className='font-semibold mb-2'>Sample Data</h4>
                <div className='overflow-x-auto'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {previewDataset.schema.map(field => (
                          <TableHead
                            key={field.name}
                            className='font-mono text-xs min-w-20'
                          >
                            {field.name}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewDataset.sampleData.map((row, index) => (
                        <TableRow
                          key={`row-${index}-${JSON.stringify(row).slice(0, 50)}`}
                        >
                          {previewDataset.schema.map(field => (
                            <TableCell
                              key={field.name}
                              className='font-mono text-xs min-w-0 break-all max-w-32'
                            >
                              {row[field.name]?.toString() || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Welcome Screen for New Users */}
      <WelcomeScreen
        tutorials={tutorialsWithCallbacks}
        onTutorialStart={handleTutorialStart}
        onSkip={() => {}}
      />

      {/* Tutorial System */}
      <TutorialOverlay
        tutorials={activeTutorial ? [activeTutorial] : []}
        onTutorialComplete={handleTutorialComplete}
        onTabChange={setActiveTab}
      />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp
        isOpen={showKeyboardHelp}
        onClose={() => setShowKeyboardHelp(false)}
      />

      <Toaster />
    </div>
  );
}

export default App;
